/**
 * TenantContext — SPA-side tenant identity.
 *
 * Resolves the storefront hostname (`window.location.host`) on mount
 * via `GET /tenants/by-host` and exposes:
 *
 *   - organisationId / subdomain / name
 *   - isMaster (apex) — UI uses this to hide the per-tenant gating
 *   - allowedProductIds (master = null, tenant = string[]) — read-only
 *     hint; the server is the source of truth for the catalog filter
 *
 * Per Rule #19 (single source) + SOP 02 §"MVVM": components consume
 * the context, never call `/tenants/by-host` directly.
 *
 * SSR fallback: when `window` is undefined we expose `isMaster: true`
 * + null allowlist so the catalog renders fully — the wrapper page
 * stays usable while the tenant resolves.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { APIBASE } from '../constants/api.constants'
import { APIROUTES } from '../constants/routes.constants'

export interface TenantCtx {
  readonly organisationId: string
  readonly subdomain: string
  readonly name: string
  readonly isMaster: boolean
  /** null on master (full catalog). Empty array → empty catalog. */
  readonly allowedProductIds: ReadonlyArray<string> | null
  readonly isLoading: boolean
}

const MASTER_FALLBACK: TenantCtx = {
  organisationId: 'a1a1a1a1-a1a1-4a1a-aa1a-a1a1a1a1a1a1',
  subdomain: 'uyyamadilu',
  name: 'Uyyamadilu',
  isMaster: true,
  allowedProductIds: null,
  isLoading: true
}

const TenantContext = createContext<TenantCtx>(MASTER_FALLBACK)

interface TenantByHostResponse {
  readonly success: boolean
  readonly data?: {
    readonly organisation_id: string
    readonly subdomain: string
    readonly name: string
    readonly is_master: boolean
    readonly allowed_product_ids: string[] | null
  }
}

export function TenantProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [tenant, setTenant] = useState<TenantCtx>(MASTER_FALLBACK)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const host = window.location.host
    const ac = new AbortController()
    ;(async () => {
      try {
        const res = await fetch(
          `${APIBASE}${APIROUTES.TENANTSBYHOST}?host=${encodeURIComponent(host)}`,
          { credentials: 'include', signal: ac.signal }
        )
        if (!res.ok) {
          // Fallback to master — keeps the SPA usable; the tenant
          // resolver only fails on a hard backend outage.
          setTenant({ ...MASTER_FALLBACK, isLoading: false })
          return
        }
        const body = (await res.json()) as TenantByHostResponse
        if (body.success && body.data !== undefined) {
          setTenant({
            organisationId: body.data.organisation_id,
            subdomain: body.data.subdomain,
            name: body.data.name,
            isMaster: body.data.is_master,
            allowedProductIds: body.data.allowed_product_ids,
            isLoading: false
          })
        } else {
          setTenant({ ...MASTER_FALLBACK, isLoading: false })
        }
      } catch (err) {
        if ((err as { name?: string }).name !== 'AbortError') {
          setTenant({ ...MASTER_FALLBACK, isLoading: false })
        }
      }
    })()
    return () => ac.abort()
  }, [])

  return <TenantContext.Provider value={tenant}>{children}</TenantContext.Provider>
}

export function useTenant(): TenantCtx {
  return useContext(TenantContext)
}
